import { Button } from "@sellar-io/components";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Icon, SpinnerLegacy } from "../../../components";
import { BUSINESS_INTEGRATION_SETTINGS } from "../../../constants/routes";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import { useBusiness } from "../../../hooks/api/v2/business/useBusiness";
import { useIntegrationCreate } from "../../../hooks/api/v2/integration/useIntegrationCreate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import stripeAppIcon from "../../../images/apps/stripe-app-icon.png";
import { IntegrationCard } from "./IntegrationCard";

export const StripeIntegrationSettings = ({
  setMessage,
}: {
  setMessage: (message: string) => void;
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { business: userBusiness } = useSellarCurrentBusiness();

  if (!userBusiness) throw new Error("Cannot find business");

  const createIntegrationMutation = useIntegrationCreate();
  const businessQuery = useBusiness(userBusiness.id);
  const accountId = businessQuery.data?.Supplier?.stripeAccountId;
  const code = searchParams.get("code");
  const integrationState = searchParams.get("state");
  const processedCodeRef = useRef<string | null>(null);

  const shouldCreateIntegration = Boolean(
    code && integrationState === "Stripe",
  );
  const [active, setActive] = useState(shouldCreateIntegration);
  const onDismiss = () => setActive(false);

  useEffect(() => {
    if (
      code &&
      integrationState === "Stripe" &&
      processedCodeRef.current !== code
    ) {
      processedCodeRef.current = code;
      const IntegrationAuthorizations: { value: string }[] = [
        {
          value: code,
        },
      ];

      createIntegrationMutation.mutate(
        {
          type: "stripe",
          IntegrationAuthorizations,
        },
        {
          onError: () => {
            onDismiss();
            setMessage(
              `There was a problem connecting to Stripe. Please contact support`,
            );
          },
          onSettled: () => {
            navigate(BUSINESS_INTEGRATION_SETTINGS);
          },
        },
      );
    }
  }, [code, createIntegrationMutation, integrationState, navigate, setMessage]);

  const loading = createIntegrationMutation.isPending;

  return (
    <>
      <IntegrationCard
        appImage={stripeAppIcon}
        title={"Stripe"}
        description={
          "Easily accept card payment from your customers for their orders on Sellar. Take payment upfront at checkout, as well as via hosted invoices."
        }
        actions={
          <>
            {!accountId && (
              <Button
                onClick={() =>
                  window.open(
                    `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${
                      import.meta.env.VITE_STRIPE_CLIENT_ID
                    }&scope=read_write&redirect_uri=${
                      import.meta.env.VITE_URL
                    }${BUSINESS_INTEGRATION_SETTINGS}&state=Stripe`,
                  )
                }
              >
                <Icon icon={"LINK"} />
                Connect
              </Button>
            )}
            <Button
              color={"secondary"}
              onClick={() =>
                window.open(
                  "https://intercom.help/sellario/en/collections/10546397-stripe-integration",
                  "_blank",
                )
              }
            >
              <Icon icon={"OPEN_IN_NEW"} />
              VIEW SETUP GUIDE
            </Button>
          </>
        }
      />
      <Dialog active={active} onDismiss={onDismiss}>
        <DialogNavigation
          onClickLeftIcon={onDismiss}
          leftIcon={"CHEVRON_DOWN"}
          title={"You're all set."}
          description={
            "You've successfully connected your Stripe account to Sellar"
          }
        />
        <DialogContent className={"p-4"}>
          {loading && <SpinnerLegacy />}
        </DialogContent>
      </Dialog>
    </>
  );
};
